import { createContext, useContext } from 'react';
import type { Impersonation } from './impersonation';

export interface AuthUser {
    id: string;
    username: string;
    displayName: string;
    isAdmin: boolean;
}

export interface AuthContextType {
    user: AuthUser | null;
    loading: boolean;
    sessionExpired: boolean;
    login: (username: string, password: string) => Promise<string | null>;
    /** Log in with a bearer token scanned from a QR code. */
    loginWithToken: (token: string) => Promise<string | null>;
    logout: () => void;
    /** Set while an administrator is signed in as somebody else. */
    impersonation: Impersonation | null;
    /** "Anmelden als": resolves to an error message, or reloads as `userId`. */
    impersonate: (userId: string, targetName: string) => Promise<string | null>;
    /** Back to the administrator's own session (reloads). */
    endImpersonation: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>({
    user: null,
    loading: true,
    sessionExpired: false,
    login: async () => 'Not initialized',
    loginWithToken: async () => 'Not initialized',
    logout: () => {},
    impersonation: null,
    impersonate: async () => 'Not initialized',
    endImpersonation: async () => {}
});

export function useAuth() {
    return useContext(AuthContext);
}
